import {
  ParsedCaptableExcel,
  ParsedCompanyDetails,
  ParsedShareholding,
} from "../types/ExcelParsingOnboarding";
import api from "./capTableApi";
import { CustomResponse } from "../types/CustomResponse";
import { useAuthStore } from "../store";

export async function uploadCompanyExcel({
  formData,
}: {
  formData: FormData;
}): Promise<any> {
  return api
    .post(`v1/company/onboarding`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    })

    .then((res) => res.data.data);
}

export type ReconstructionStatus =
  | "pending"
  | "processing"
  | "completed"
  | "failed";

export type Stage4ReconstructionResult = {
  jobId?: string;
  status: ReconstructionStatus;
  statusEndpoint?: string;
  error?: string | null;
  errorMessage?: string | null;
  downloadUrl?: string | null;
  downloadUrlExpiresAt?: string | null;
  filename?: string;
  sourceFilename?: string;
  sessionId?: string;
  createdAt?: string;
  completedAt?: string;
};

export type Stage4BatchFile = {
  jobId: string;
  filename: string;
  status: ReconstructionStatus;
  statusEndpoint?: string;
  error?: string | null;
};

export type Stage4BatchStatus =
  | "pending"
  | "processing"
  | "completed"
  | "completed_with_errors"
  | "failed";

export type Stage4Batch = {
  batchId: string;
  status: Stage4BatchStatus;
  totalFiles: number;
  pendingFiles: number;
  processingFiles: number;
  completedFiles: number;
  failedFiles: number;
  files: Stage4BatchFile[];
};

export type Stage4Notification = {
  id: string;
  batchId: string;
  message?: string;
  isRead: boolean;
  type?: "workbook" | "batch" | string;
  filename?: string;
  createdAt?: string;
  status?: string;
};

export type Stage4PreviewSheet = {
  name: string;
  rowCount: number;
  columnCount: number;
};

export type Stage4SheetPreview = {
  columns: string[];
  rows: Array<Array<string | number | null>>;
  offset: number;
  limit: number;
  hasMore: boolean;
};

const unwrapTaskResult = (response: any): Stage4ReconstructionResult =>
  response?.data?.data || response?.data || response;

const normalizeStage4Result = (response: any): Stage4ReconstructionResult => {
  const result = unwrapTaskResult(response);
  const status = String(result?.status || "")
    .trim()
    .toLowerCase();
  return {
    ...result,
    // Treat the persisted API value as canonical even if a proxy adds
    // whitespace or changes its casing.
    status: status as ReconstructionStatus,
    // The worker persists `errorMessage`; keep one stable field for callers.
    error: result.error || result.errorMessage || null,
  };
};

/** The Stage-4 routes use standard Bearer authentication. */
const stage4AuthHeaders = () => {
  const token =
    sessionStorage.getItem("accesstoken") ||
    useAuthStore.getState().accessToken;
  if (!token) return undefined;

  return {
    Authorization: `Bearer ${token.replace(/^Bearer\s+/i, "")}`,
  };
};

export async function reconstructStage4(
  file: File,
  templateUrl?: string
): Promise<Stage4ReconstructionResult> {
  const formData = new FormData();
  formData.append("file", file);
  if (templateUrl) formData.append("templateUrl", templateUrl);
  const response = await api.post(
    "/v1/taskManager/reconstruct-stage4",
    formData,
    {
      headers: {
        // Remove the client's JSON default so the browser supplies the
        // multipart boundary required by multer's `upload.single("file")`.
        "Content-Type": undefined,
        ...stage4AuthHeaders(),
      },
    }
  );
  return normalizeStage4Result(response);
}

export async function reconstructStage4Batch(
  files: File[],
  templateUrl?: string
): Promise<Stage4Batch> {
  const formData = new FormData();
  files.forEach((file) => formData.append("files", file));
  if (templateUrl) formData.append("templateUrl", templateUrl);
  const response = await api.post(
    "/v1/taskManager/reconstruct-stage4/batch",
    formData,
    {
      headers: { "Content-Type": undefined, ...stage4AuthHeaders() },
    }
  );
  return unwrapTaskResult(response) as Stage4Batch;
}

export async function getStage4Batch(batchId: string): Promise<Stage4Batch> {
  const response = await api.get(
    `/v1/taskManager/reconstruct-stage4/batches/${batchId}`,
    {
      headers: stage4AuthHeaders(),
    }
  );
  return unwrapTaskResult(response) as Stage4Batch;
}

export async function retryStage4BatchJob(
  batchId: string,
  jobId: string
): Promise<Stage4Batch> {
  const response = await api.post(
    `/v1/taskManager/reconstruct-stage4/batches/${batchId}/jobs/${jobId}/retry`,
    undefined,
    { headers: stage4AuthHeaders() }
  );
  return unwrapTaskResult(response) as Stage4Batch;
}

export async function getStage4Notifications(): Promise<Stage4Notification[]> {
  const response = await api.get(
    "/v1/taskManager/reconstruct-stage4/notifications",
    {
      headers: stage4AuthHeaders(),
      params: { unread: true },
    }
  );
  const data: any = unwrapTaskResult(response);
  return data.notifications || data || [];
}

export async function markStage4NotificationRead(
  notificationId: string
): Promise<void> {
  await api.patch(
    `/v1/taskManager/reconstruct-stage4/notifications/${notificationId}/read`,
    undefined,
    {
      headers: stage4AuthHeaders(),
    }
  );
}

export async function getStage4Reconstruction(
  statusEndpoint: string
): Promise<Stage4ReconstructionResult> {
  const response = await api.get(statusEndpoint, {
    headers: stage4AuthHeaders(),
  });
  return normalizeStage4Result(response);
}

export async function getStage4DownloadUrl(
  jobId: string
): Promise<
  Pick<
    Stage4ReconstructionResult,
    "downloadUrl" | "downloadUrlExpiresAt" | "filename"
  >
> {
  const response = await api.get(
    `/v1/taskManager/reconstruct-stage4/${jobId}/download-url`,
    { headers: stage4AuthHeaders() }
  );
  return unwrapTaskResult(response);
}

export async function getStage4PreviewSheets(
  jobId: string
): Promise<Stage4PreviewSheet[]> {
  const response = await api.get(
    `/v1/taskManager/reconstruct-stage4/${jobId}/preview`,
    { headers: stage4AuthHeaders() }
  );
  return (unwrapTaskResult(response) as any).sheets || [];
}

export async function getStage4SheetPreview(
  jobId: string,
  sheet: string,
  offset = 0,
  limit = 50
): Promise<Stage4SheetPreview> {
  const endpoint = `/v1/taskManager/reconstruct-stage4/${jobId}/preview`;
  const request = (params: Record<string, string | number>) =>
    api.get(endpoint, { headers: stage4AuthHeaders(), params });
  try {
    const response = await request({ sheet, offset, limit });
    return (unwrapTaskResult(response) as any).sheet;
  } catch (error: any) {
    // Some deployed Stage-4 workers use `sheetName`; support both contracts
    // while the API versions are rolling out.
    if (![400, 404, 422].includes(error?.response?.status)) throw error;
    const response = await request({ sheetName: sheet, offset, limit });
    return (unwrapTaskResult(response) as any).sheet;
  }
}

export async function parseCaptableExcel({
  formData,
  companyId,
}: {
  formData: FormData;
  companyId?: string;
}): Promise<ParsedCaptableExcel> {
  if (companyId) {
    formData.append("companyId", companyId);
  }
  return api
    .post(`v1/taskManager/parse-captable`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    })
    .then((res) => res.data.data);
}

export type CaptableAIBatchStatus = {
  batchId: string;
  companyId?: string | null;
  status?: string;
  notificationRead?: boolean;
  isNotificationRead?: boolean;
  createdAt?: string;
  updatedAt?: string;
  counters?: Partial<
    Record<"queued" | "waiting" | "processing" | "completed" | "failed", number>
  >;
  files: Array<{
    fileId?: string;
    id?: string;
    filename?: string;
    fileName?: string;
    name?: string;
    originalName?: string;
    originalFilename?: string;
    size?: number;
    fileSize?: number;
    sizeBytes?: number;
    status: string;
    stage?: string;
    redactionAttempts?: number;
    llmAttempts?: number;
    retryAttempts?: number;
    errorStage?: string;
    errorMessage?: string;
    data?: ParsedCaptableExcel;
    error?: string;
  }>;
};

export type CaptableAiNotification = {
  id: string;
  batchId: string;
  companyId: string | null;
  status:
    | "PROCESSING"
    | "ACTION_REQUIRED"
    | "READY_TO_ONBOARD"
    | "ONBOARDING"
    | "COMPLETED"
    | "COMPLETED_WITH_ERRORS"
    | "FAILED";
  message: string;
  isRead: boolean;
  createdAt: string;
  category?: string;
  workflowStatus?: string;
};

export const isCompanyOnboardingNotification = (
  notification: CaptableAiNotification
) =>
  /AI_COMPANY|COMPANY_ONBOARDING|ACTION_REQUIRED|NEEDS_INPUT|READY_TO_ONBOARD|details required|companies ready|onboarding completed/i.test(
    `${notification.category || ""} ${notification.workflowStatus || ""} ${
      notification.status
    } ${notification.message}`
  );

function normalizeCaptableAiNotification(value: any): CaptableAiNotification {
  const container = value?.data || value || {};
  const raw = container?.notification || value?.notification || container;
  return {
    id: String(raw.id || raw.notificationId || raw.notification_id || ""),
    batchId: String(raw.batchId || raw.batch_id || ""),
    companyId: raw.companyId ?? raw.company_id ?? null,
    status: String(
      raw.status || "FAILED"
    ).toUpperCase() as CaptableAiNotification["status"],
    message: raw.message || raw.errorMessage || "Cap table processing update",
    isRead: Boolean(raw.isRead ?? raw.is_read),
    createdAt: raw.createdAt || raw.created_at || new Date().toISOString(),
    category:
      raw.category ||
      raw.type ||
      raw.eventType ||
      raw.event_type ||
      container?.eventType ||
      container?.event_type ||
      value?.eventType,
    workflowStatus:
      raw.workflowStatus ||
      raw.workflow_status ||
      container?.workflowStatus ||
      container?.workflow_status,
  };
}

export async function parseCaptableAI({
  formData,
  companyId,
}: {
  formData: FormData;
  companyId?: string;
}): Promise<{ batchId: string }> {
  if (companyId) formData.append("companyId", companyId);
  const token = sessionStorage.getItem("accesstoken");
  return api
    .post(`v1/taskManager/parse-captable-ai/batch`, formData, {
      // The shared client defaults POST requests to application/json. Explicitly
      // remove that inherited value so the browser can add multipart/form-data
      // with the required boundary for FormData.
      headers: {
        "Content-Type": undefined,
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    })
    .then((res) => {
      if (res.status !== 202) {
        throw new Error(
          `Expected batch upload to return 202, received ${res.status}`
        );
      }
      return res.data.data;
    });
}

export async function getActiveCaptableAIBatches(): Promise<
  CaptableAIBatchStatus[]
> {
  return api
    .get(`v1/taskManager/parse-captable-ai/batches`, {
      params: { active: true },
    })
    .then((res) => res.data.data?.batches || res.data.data || []);
}

export async function getCaptableAIBatchStatus(
  batchId: string
): Promise<CaptableAIBatchStatus> {
  return api
    .get(`v1/taskManager/parse-captable-ai/batch/${batchId}`)
    .then((res) => res.data.data);
}

export async function getCaptableAINotifications(
  unread?: boolean
): Promise<CaptableAiNotification[]> {
  return api
    .get(`v1/taskManager/parse-captable-ai/notifications`, {
      params: { ...(unread === undefined ? {} : { unread }), limit: 50 },
    })
    .then((res) =>
      (res.data.data?.notifications || res.data.data || [])
        .map(normalizeCaptableAiNotification)
        .filter((notification: CaptableAiNotification) => notification.id)
    );
}

export async function markCaptableAINotificationRead(
  notificationId: string
): Promise<void> {
  await api.patch(
    `v1/taskManager/parse-captable-ai/notifications/${notificationId}/read`
  );
}

export async function retryCaptableAIFile(
  batchId: string,
  fileId: string
): Promise<CaptableAIBatchStatus | undefined> {
  return api
    .post(
      `v1/taskManager/parse-captable-ai/batch/${batchId}/files/${fileId}/retry`
    )
    .then((res) => res.data.data);
}

export async function watchCaptableAIBatch(
  batchId: string,
  token: string,
  onUpdate: (batch: CaptableAIBatchStatus & { status?: string }) => void,
  signal?: AbortSignal
): Promise<void> {
  const authToken =
    token ||
    sessionStorage.getItem("accesstoken") ||
    useAuthStore.getState().accessToken ||
    "";
  const baseUrl = (
    api.defaults.baseURL ||
    process.env.REACT_APP_NEW_CAPTABLE_API ||
    ""
  ).replace(/\/$/, "");
  const response = await fetch(
    `${baseUrl}/v1/taskManager/parse-captable-ai/batch/${batchId}/events`,
    {
      headers: {
        Authorization: `Bearer ${authToken.replace(/^Bearer\s+/i, "")}`,
        Accept: "text/event-stream",
      },
      signal,
    }
  );
  if (!response.ok || !response.body) {
    throw new Error("Unable to connect to batch events");
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  try {
    // SSE streams intentionally wait for an unbounded number of updates.
    // eslint-disable-next-line no-constant-condition
    while (true) {
      // eslint-disable-next-line no-await-in-loop
      const { value, done } = await reader.read();
      if (done) return;
      buffer += decoder.decode(value, { stream: true });
      const events = buffer.split(/\r?\n\r?\n/);
      buffer = events.pop() || "";
      for (const event of events) {
        const dataLine = event
          .split(/\r?\n/)
          .find((line) => line.startsWith("data: "));
        if (!dataLine) continue; // eslint-disable-line no-continue
        const payload = JSON.parse(dataLine.slice(6));
        const batch = payload.data;
        onUpdate(batch);
        const terminal =
          ["COMPLETED", "COMPLETED_WITH_ERRORS", "FAILED"].includes(
            batch.status
          ) ||
          batch.files?.every((file: { status: string }) =>
            ["COMPLETED", "FAILED"].includes(file.status)
          );
        if (terminal) return;
      }
    }
  } finally {
    await reader.cancel();
    reader.releaseLock();
  }
}

export async function watchCaptableAINotifications(
  onNotification: (notification: CaptableAiNotification) => void,
  signal?: AbortSignal,
  onConnected?: () => void
): Promise<void> {
  const token =
    sessionStorage.getItem("accesstoken") ||
    useAuthStore.getState().accessToken ||
    "";
  const baseUrl = (
    api.defaults.baseURL ||
    process.env.REACT_APP_NEW_CAPTABLE_API ||
    ""
  ).replace(/\/$/, "");
  const response = await fetch(
    `${baseUrl}/v1/taskManager/parse-captable-ai/notification-events`,
    {
      headers: {
        Authorization: `Bearer ${token.replace(/^Bearer\s+/i, "")}`,
        Accept: "text/event-stream",
      },
      signal,
    }
  );
  if (!response.ok || !response.body) {
    throw new Error("Unable to connect to notification events");
  }
  onConnected?.();

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  try {
    // eslint-disable-next-line no-constant-condition
    while (true) {
      // eslint-disable-next-line no-await-in-loop
      const { value, done } = await reader.read();
      if (done) return;
      buffer += decoder.decode(value, { stream: true });
      const events = buffer.split(/\r?\n\r?\n/);
      buffer = events.pop() || "";
      events.forEach((eventBlock) => {
        const lines = eventBlock.split(/\r?\n/);
        const eventName = lines
          .find((line) => line.startsWith("event:"))
          ?.slice(6)
          .trim();
        const data = lines
          .filter((line) => line.startsWith("data:"))
          .map((line) => line.slice(5).trim())
          .join("\n");
        if (!data) return;
        const payload = JSON.parse(data);
        const notification = normalizeCaptableAiNotification({
          ...payload,
          eventType: eventName,
        });
        if (notification?.id) onNotification(notification);
      });
    }
  } finally {
    await reader.cancel();
    reader.releaseLock();
  }
}

export type OnboardFromParsedPayload = {
  companyDetails: ParsedCompanyDetails;
  shareholding: ParsedShareholding[];
  emails?: string[];
  companyId?: string;
};

export type OnboardFromParsedResponse = {
  companyId: string;
};

export async function removeCaptableSourceFile({
  documentId,
}: {
  documentId: string;
}): Promise<any> {
  return api
    .delete(`v1/taskManager/remove-captable`, {
      params: { documentId },
    })
    .then((res) => res.data.data);
}

export async function removeTaskManagerCompany({
  companyIds,
}: {
  companyIds: string[];
}): Promise<DeleteCompanyResult> {
  const token =
    sessionStorage.getItem("accesstoken") ||
    sessionStorage.getItem("accessToken");

  return api
    .delete("v1/taskManager/remove-company", {
      data: { companyIds },
      headers: token
        ? { Authorization: /^Bearer\s+/i.test(token) ? token : `Bearer ${token}` }
        : undefined,
    })
    .then((res) => res.data.data);
}

export interface DeleteCompanyResult {
  deleted?: boolean;
  deletedCompanyIds?: string[];
  blockedCompanies?: Array<{
    companyId: string;
    projects?: DeleteCompanyResult["projects"];
  }>;
  projects?: Array<{
    id: string;
    name: string;
    tasks: Array<{
      id: string;
      name: string;
      startDate?: string | null;
      endDate?: string | null;
    }>;
  }>;
}

export async function bulkOnboardCompanies({
  file,
  emails,
}: {
  file: File;
  // Same "grant every account user access" list AddCompanyModal sends via
  // its `emails` field — applied to every company in the sheet.
  emails?: string[];
}): Promise<any> {
  const formData = new FormData();
  formData.append("file", file);
  // Appended once per email under the same field name (rather than one
  // JSON-stringified blob) so the multipart parser collects it as a real
  // list — a naive split(",") on a single JSON string mangles addresses.
  if (emails && emails.length > 0) {
    emails.forEach((email) => formData.append("emails", email));
  }
  return api
    .post(`v1/taskManager/onboard-stage3-list`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    })
    .then((res) => res.data.data);
}

export async function onboardFromParsed({
  payload,
  file,
  isPrivate,
}: {
  payload: OnboardFromParsedPayload;
  file?: File;
  isPrivate?: boolean;
}): Promise<OnboardFromParsedResponse> {
  const params = isPrivate === undefined ? undefined : { isPrivate };

  if (file) {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("data", JSON.stringify(payload));
    return api
      .post(`v1/taskManager/onboard-from-parsed`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        params,
      })
      .then((res) => res.data.data);
  }

  return api
    .post(`v1/taskManager/onboard-from-parsed`, payload, { params })
    .then((res) => res.data.data);
}
